import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { warrantyApi } from '../../services/api';

interface WarrantyRegistration {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  installDate: string;
  installerName?: string;
  installerCompany?: string;
  status: string;
  registeredAt: string;
  motorWarrantyExpiresAt: string;
  fabricWarrantyExpiresAt: string;
  frameWarrantyExpiresAt: string;
  photoUrls: string[];
}

const statusColors: Record<string, string> = {
  registered: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-800',
  voided: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  registered: 'Registered',
  active: 'Active',
  expired: 'Expired',
  voided: 'Voided',
};

export const MyWarrantiesPage: React.FC = () => {
  const [warranties, setWarranties] = useState<WarrantyRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Selected warranty for detail view
  const [selectedWarranty, setSelectedWarranty] = useState<WarrantyRegistration | null>(null);

  useEffect(() => {
    fetchWarranties();
  }, [page, searchQuery, statusFilter]);

  const fetchWarranties = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = { page, pageSize: 20 };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await warrantyApi.listRegistrations(params);
      setWarranties(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load warranties');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getWarrantyStatus = (warranty: WarrantyRegistration) => {
    const now = new Date();
    const motorExpiry = new Date(warranty.motorWarrantyExpiresAt);
    const fabricExpiry = new Date(warranty.fabricWarrantyExpiresAt);

    if (warranty.status === 'voided') return 'voided';
    if (now > motorExpiry && now > fabricExpiry) return 'expired';
    return 'active';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My Warranties</h1>
          <p className="text-gray-400">View and manage registered warranties</p>
        </div>
        <Link to="/warranty/register" className="btn-primary">
          + Register Warranty
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by order number, customer name, or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="input-field w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="select-field"
          >
            <option value="all">All Statuses</option>
            <option value="registered">Registered</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <div className="text-sm text-gray-400">
          Showing {warranties.length} of {total} warranties
        </div>
      )}

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="card p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-keder-yellow mx-auto"></div>
          <p className="mt-2 text-gray-400">Loading warranties...</p>
        </div>
      ) : warranties.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-gray-400 text-5xl mb-4">&#128203;</div>
          <h3 className="text-lg font-medium text-white">No warranties found</h3>
          <p className="text-gray-400 mt-1">
            {searchQuery || statusFilter !== 'all'
              ? 'No warranties match your search criteria.'
              : "You haven't registered any warranties yet."}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Link to="/warranty/register" className="inline-block mt-4 btn-primary">
              Register Your First Warranty
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Warranties Table */}
          <div className="card overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-keder-gray">
                <thead className="bg-keder-gray/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Install Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Registered
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-keder-gray/50">
                  {warranties.map((warranty) => {
                    const displayStatus = getWarrantyStatus(warranty);
                    return (
                      <tr key={warranty.id} className="hover:bg-keder-gray/20">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-keder-yellow font-medium">
                            {warranty.orderNumber || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-white">{warranty.customerName}</p>
                            <p className="text-gray-400 text-sm">{warranty.customerEmail}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                          {formatDate(warranty.installDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[displayStatus]}`}
                          >
                            {statusLabels[displayStatus]}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-400 text-sm">
                          {formatDate(warranty.registeredAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => setSelectedWarranty(warranty)}
                            className="text-keder-yellow hover:text-yellow-400"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-keder-gray/30 px-6 py-4 flex items-center justify-between border-t border-keder-gray">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedWarranty && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold text-white">Warranty Details</h2>
                <button
                  onClick={() => setSelectedWarranty(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Order Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Order Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-400 text-sm">Order Number:</span>
                      <p className="text-white">{selectedWarranty.orderNumber || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Install Date:</span>
                      <p className="text-white">{formatDate(selectedWarranty.installDate)}</p>
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-400 text-sm">Name:</span>
                      <p className="text-white">{selectedWarranty.customerName}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Email:</span>
                      <p className="text-white">{selectedWarranty.customerEmail}</p>
                    </div>
                    {selectedWarranty.customerPhone && (
                      <div>
                        <span className="text-gray-400 text-sm">Phone:</span>
                        <p className="text-white">{selectedWarranty.customerPhone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Warranty Coverage */}
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Warranty Coverage</h3>
                  <div className="bg-keder-gray/30 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-gray-400 text-sm">Motor</p>
                        <p className="text-white font-medium">5 Years</p>
                        <p className="text-gray-400 text-xs">
                          Expires: {formatDate(selectedWarranty.motorWarrantyExpiresAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Fabric</p>
                        <p className="text-white font-medium">10 Years</p>
                        <p className="text-gray-400 text-xs">
                          Expires: {formatDate(selectedWarranty.fabricWarrantyExpiresAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Frame</p>
                        <p className="text-white font-medium">Lifetime</p>
                        <p className="text-gray-400 text-xs">N/A</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Installation Photos */}
                {selectedWarranty.photoUrls && selectedWarranty.photoUrls.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-400 mb-2">Installation Photos</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {selectedWarranty.photoUrls.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={url}
                            alt={`Installation ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Installer Info */}
                {(selectedWarranty.installerName || selectedWarranty.installerCompany) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-400 mb-2">Installer Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedWarranty.installerName && (
                        <div>
                          <span className="text-gray-400 text-sm">Installer:</span>
                          <p className="text-white">{selectedWarranty.installerName}</p>
                        </div>
                      )}
                      {selectedWarranty.installerCompany && (
                        <div>
                          <span className="text-gray-400 text-sm">Company:</span>
                          <p className="text-white">{selectedWarranty.installerCompany}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button onClick={() => setSelectedWarranty(null)} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWarrantiesPage;
